import React, { useEffect, useRef } from 'react';

const WHATSAPP_URL = 'https://wa.me/447716089293';

export default function HeroAndStats() {
    const heroContentRef = useRef(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (prefersReduced.matches) return;

        const node = heroContentRef.current;
        if (!node) return;

        let ticking = false;
        const update = () => {
            const progress = Math.min(1, Math.max(0, window.scrollY / (window.innerHeight * 0.75)));
            node.style.transform = `translate3d(0, ${-progress * 48}px, 0)`;
            node.style.opacity = `${1 - progress * 0.85}`;
            ticking = false;
        };
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(update);
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        update();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <React.Fragment>
            <>
                <section className="relative min-h-[100dvh] bg-gray-900 text-white overflow-hidden">
                    {/* Hero photograph with slow Ken Burns drift */}
                    <div className="absolute inset-0 z-0">
                        <picture>
                            <source srcSet="/flow-assets/content/hero-coffee-cherries.webp" type="image/webp" />
                            <img
                                src="/flow-assets/content/hero-coffee-cherries.jpg"
                                alt=""
                                aria-hidden="true"
                                fetchpriority="high"
                                className="absolute inset-0 w-full h-full object-cover animate-hero-drift will-change-transform motion-reduce:animate-none"
                            />
                        </picture>
                        {/* Darker on the left where the copy sits, lets the cherries breathe on the right */}
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/75 to-gray-900/60 md:via-gray-900/65 md:to-gray-900/25" />
                        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-gray-900 to-transparent" />
                    </div>

                    {/* Hero content, left aligned over the darker side of the photo */}
                    <div ref={heroContentRef} className="relative z-10 flex items-center min-h-[100dvh] pt-28 pb-16 lg:pt-36 will-change-transform">
                        <div className="container mx-auto px-4 lg:px-8">
                            <div className="max-w-6xl mx-auto">
                            <div className="max-w-2xl">
                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-400 mb-5 animate-rise motion-reduce:animate-none">
                                    Home of Moonshine Coffee
                                </p>
                                <h1 className="font-heading font-medium text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-8">
                                    <span className="block text-gold-400 animate-rise motion-reduce:animate-none">Uganda Robusta</span>
                                    <span className="block text-white animate-rise motion-reduce:animate-none" style={{ animationDelay: '140ms' }}>from Mubarak Farm</span>
                                </h1>
                                <span
                                    aria-hidden="true"
                                    className="block h-px w-20 bg-gold-400 origin-left mb-8 animate-mark-draw motion-reduce:animate-none motion-reduce:scale-x-100"
                                />
                                <p
                                    className="max-w-xl text-lg sm:text-xl text-white/80 mb-10 leading-relaxed animate-rise motion-reduce:animate-none"
                                    style={{ animationDelay: '360ms' }}
                                >
                                    Grown in Luweero, Uganda, stocked in Karachi and Kampala, and delivered in 2-3 days.
                                </p>

                                <div
                                    className="flex flex-wrap items-baseline gap-x-8 gap-y-2 mb-10 text-sm text-white/80 animate-rise motion-reduce:animate-none"
                                    style={{ animationDelay: '520ms' }}
                                >
                                    <span><span className="text-gold-400 font-heading text-base">Feb 2026</span> harvest, customs cleared</span>
                                    <span><span className="text-gold-400 font-heading text-base">11.8%</span> moisture content</span>
                                    <span><span className="text-gold-400 font-heading text-base">25+ tons</span> sold to date</span>
                                </div>

                                <div
                                    className="flex flex-col sm:flex-row gap-4 animate-rise motion-reduce:animate-none"
                                    style={{ animationDelay: '680ms' }}
                                >
                                    <a
                                        href={WHATSAPP_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center py-4 px-8 text-base font-bold text-gray-900 bg-gold-400 hover:bg-gold-300 rounded-full transition-[background-color,transform] duration-300 ease-out active:scale-[0.97]"
                                        aria-label="Order Now on WhatsApp"
                                    >
                                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                        </svg>
                                        <span>Order Now</span>
                                    </a>
                                    <a
                                        href="#aboutourcoffee"
                                        className="group inline-flex items-center justify-center py-4 px-8 text-base font-bold border border-white/40 text-white hover:bg-white hover:text-gray-900 hover:border-white rounded-full transition-colors duration-300 ease-out active:scale-[0.97]"
                                        aria-label="Learn more about our coffee beans"
                                    >
                                        <span>Learn More</span>
                                        <svg className="w-5 h-5 ml-2 transition-transform duration-300 ease-out group-hover:translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="relative py-20 lg:py-28 bg-gray-900">
                    <div className="container mx-auto px-4 lg:px-8">
                        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
                            {/* Lede stat takes 5/12, dominates the row */}
                            <div className="lg:col-span-5">
                                <p className="font-heading text-gold-400 text-7xl lg:text-8xl leading-none">2x</p>
                                <p className="mt-4 text-lg text-gray-300 max-w-xs">
                                    Robusta carries roughly twice the caffeine of Arabica. We don't blend it down.
                                </p>
                            </div>

                            <dl className="lg:col-span-7 divide-y divide-gray-800">
                                <div className="grid grid-cols-12 gap-4 py-5">
                                    <dt className="col-span-4 font-heading text-gold-400 text-2xl lg:text-3xl">SC-18</dt>
                                    <dd className="col-span-8 text-gray-300 text-base lg:text-lg leading-snug">
                                        Top grade in our spec sheet. Screen 18 is the largest, cleanest cut of the harvest.
                                    </dd>
                                </div>
                                <div className="grid grid-cols-12 gap-4 py-5">
                                    <dt className="col-span-4 font-heading text-gold-400 text-2xl lg:text-3xl">1 farm</dt>
                                    <dd className="col-span-8 text-gray-300 text-base lg:text-lg leading-snug">
                                        Single-estate, Halal-suitable, no additives. Grown and processed in Nabutaka.
                                    </dd>
                                </div>
                                <div className="grid grid-cols-12 gap-4 py-5">
                                    <dt className="col-span-4 font-heading text-gold-400 text-2xl lg:text-3xl">0</dt>
                                    <dd className="col-span-8 text-gray-300 text-base lg:text-lg leading-snug">
                                        Middlemen between the farm and the warehouse you order from.
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </section>
            </>
        </React.Fragment>
    );
}
